import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Modal, ModalFooter } from './Modal';
import { useWorkScheduleModelStore } from '../../store/useWorkScheduleModelStore';
import { useEmployeeStore } from '../../store/useEmployeeStore';
import { useToastStore } from '../../store/useToastStore';
import { showApiError } from '../../errors';

interface AssignWorkScheduleModelDialogProps {
  isOpen: boolean;
  employeeId: string;
  employeeName: string;
  currentModelId?: string | null;
  onClose: () => void;
  /** Called after a successful assignment so callers can refetch their data. */
  onAssigned?: (workScheduleModelId: string) => void | Promise<void>;
}

/**
 * Assigns a work schedule model to an employee via the dedicated endpoint
 * (PUT /employees/{id}/work-schedule-model). Opened from the Dienstplanung when an
 * employee cannot be scheduled because no model is assigned.
 */
export function AssignWorkScheduleModelDialog({
  isOpen,
  employeeId,
  employeeName,
  currentModelId,
  onClose,
  onAssigned,
}: AssignWorkScheduleModelDialogProps) {
  const { t } = useTranslation('workScheduleModels');
  const { t: tCommon } = useTranslation('common');
  const { models, fetchModels } = useWorkScheduleModelStore();
  const { assignWorkScheduleModel } = useEmployeeStore();
  const { addToast } = useToastStore();

  const [selectedModelId, setSelectedModelId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchModels();
    }
  }, [isOpen, fetchModels]);

  const activeModels = models.filter((m) => m.isActive !== false);

  // An inactive current model is not in the list; preselecting it would submit a model
  // the server no longer offers (FACF-60).
  useEffect(() => {
    if (isOpen) {
      const currentIsActive = activeModels.some((m) => m.workScheduleModelId === currentModelId);
      setSelectedModelId(currentIsActive && currentModelId ? currentModelId : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the list or the current model changes
  }, [isOpen, currentModelId, models]);

  const handleAssign = async () => {
    if (!selectedModelId) return;
    setSaving(true);
    try {
      await assignWorkScheduleModel(employeeId, selectedModelId);
      addToast(t('assign.success'), 'success');
      await onAssigned?.(selectedModelId);
      onClose();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('assign.title')}
      size="sm"
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleAssign}
          confirmText={t('assign.confirm')}
          cancelText={tCommon('actions.cancel')}
          confirmDisabled={!selectedModelId}
          loading={saving}
        />
      }
    >
      <div className="space-y-4">
        {!currentModelId && (
          <p className="text-[13px] text-slate-700">
            {t('assign.missingHint', { name: employeeName })}
          </p>
        )}

        {activeModels.length === 0 ? (
          <p className="text-[13px] text-slate-500">
            {t('assign.noModels')}{' '}
            <Link
              to="/work-schedule-models"
              className="text-[#00A871] hover:underline font-medium"
              onClick={onClose}
            >
              {t('assign.goToModels')}
            </Link>
          </p>
        ) : (
          <div>
            <label
              htmlFor="assign-work-schedule-model"
              className="block text-[12px] font-medium text-slate-700 mb-1"
            >
              {t('assign.selectLabel')}
            </label>
            <select
              id="assign-work-schedule-model"
              value={selectedModelId}
              onChange={(e) => setSelectedModelId(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-[13px] text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]"
            >
              <option value="">{t('assign.selectPlaceholder')}</option>
              {activeModels.map((model) => (
                <option key={model.workScheduleModelId} value={model.workScheduleModelId}>
                  {model.name} ({model.weeklyHours} h)
                </option>
              ))}
            </select>
          </div>
        )}

        <p className="text-[12px] text-slate-500">{t('assign.regenerationHint')}</p>
      </div>
    </Modal>
  );
}

export default AssignWorkScheduleModelDialog;
